'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

import { trackSignal, trackSignalWithAck } from '@/lib/api/signals';
import {
  JourneyReliability,
  msUntilNextShanghaiMidnight,
} from '@/lib/analytics/journeyReliability';
import type { ReliabilityStorage } from '@/lib/analytics/journeyReliability';
import { useAuthStore } from '@/stores/authStore';

function createId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `journey-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function safeSessionStorage(): ReliabilityStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function safeLocalStorage(): ReliabilityStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function queryKeys(): string[] {
  try {
    return Array.from(new Set(new URLSearchParams(window.location.search).keys())).sort();
  } catch {
    return [];
  }
}

function referrerHost(): string | null {
  try {
    return document.referrer ? new URL(document.referrer).hostname : null;
  } catch {
    return null;
  }
}

function deviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (window.innerWidth < 640) return 'mobile';
  if (window.innerWidth < 1024) return 'tablet';
  return 'desktop';
}

export function UserJourneyTracker() {
  const pathname = usePathname();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  // 按 userId 稳定复用一个 JourneyReliability：pathname 变化只更新 route/meta 与重绑监听，
  // 不重建实例（inflight 的 session/daily attempt 因此跨路由保活）；用户/鉴权失效时清实例
  const reliabilityRef = useRef<{ userId: string; instance: JourneyReliability } | null>(null);
  const authGenerationRef = useRef(0);
  const routeRef = useRef<string | null>(null);
  const routeStartedAtRef = useRef<number>(Date.now());
  const pageActiveRef = useRef(false);

  useEffect(() => {
    const canTrack = Boolean(isAuthenticated && user?.id && !user?.isAdmin);
    if (!canTrack || !user?.id || !pathname || pathname.startsWith('/admin')) {
      authGenerationRef.current += 1;
      reliabilityRef.current = null;
      routeRef.current = null;
      pageActiveRef.current = false;
      return;
    }

    const userId = user.id;
    const existingReliability = reliabilityRef.current?.userId === userId
      ? reliabilityRef.current.instance
      : null;
    const generation = existingReliability
      ? authGenerationRef.current
      : authGenerationRef.current + 1;
    if (!existingReliability) authGenerationRef.current = generation;
    const isCurrentAuth = () => {
      const auth = useAuthStore.getState();
      return (
        authGenerationRef.current === generation
        && auth.isAuthenticated
        && auth.user?.id === userId
        && auth.user?.isAdmin !== true
      );
    };
    const reliability = existingReliability
      ?? new JourneyReliability(userId, safeSessionStorage(), safeLocalStorage(), {
        now: () => Date.now(),
        createId,
        isActive: isCurrentAuth,
        send: (context) => trackSignalWithAck('journey', context, undefined, isCurrentAuth),
        // meta getter 读 ref：路由变化只影响后续读取的元信息，不需要重建实例
        sessionStartedMeta: () => ({
          client_surface: 'web',
          route: routeRef.current ?? pathname,
          query_keys: queryKeys(),
          referrer_host: referrerHost(),
          locale: document.documentElement.lang || null,
          device_type: deviceType(),
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
        }),
      });
    reliabilityRef.current = { userId, instance: reliability };

    // 普通 journey 事件仍走 fire-and-forget trackSignal（非 journey API 行为不变）。
    // 不 await daily 网络：先启动 durable attempt（单飞、失败按原值重试），随后同步
    // beginEvent 并立即发送 —— page_exit/pagehide 不被慢网阻塞，网络耗时不制造会话轮转
    const emit = (
      eventName: string,
      context: Record<string, unknown>,
      projectId?: string,
    ) => {
      try {
        if (!isCurrentAuth()) return;
        void reliability.ensureDailyActive();
        const event = reliability.beginEvent();
        trackSignal('journey', {
          schema_version: 1,
          event_name: eventName,
          session_id: event.id,
          sequence: event.sequence,
          occurred_at: event.occurredAt,
          ...context,
        }, projectId, isCurrentAuth);
      } catch {
        // 旁路埋点任何失败都不允许外抛
      }
    };

    const emitExit = (reason: 'route_change' | 'hidden' | 'unload') => {
      if (!pageActiveRef.current || !routeRef.current) return;
      pageActiveRef.current = false;
      emit('page_exit', {
        route: routeRef.current,
        duration_ms: Math.max(0, Date.now() - routeStartedAtRef.current),
        navigation_type: reason,
      });
    };

    // 跨上海自然日 timer：仅 visible 时存在；hidden 立即清除，visible 恢复时重建
    let midnightTimer: number | null = null;
    const clearMidnightTimer = () => {
      if (midnightTimer !== null) {
        window.clearTimeout(midnightTimer);
        midnightTimer = null;
      }
    };
    const scheduleMidnightEnsure = () => {
      clearMidnightTimer();
      midnightTimer = window.setTimeout(() => {
        midnightTimer = null;
        // 仅 visible 计活跃：触发时仍 visible 才确保新一天的 daily_active 并续期
        if (document.visibilityState === 'visible') {
          void reliability.ensureDailyActive();
          scheduleMidnightEnsure();
        }
      }, msUntilNextShanghaiMidnight(Date.now()));
    };

    if (routeRef.current && routeRef.current !== pathname) {
      emitExit('route_change');
    }

    if (routeRef.current !== pathname || !pageActiveRef.current) {
      const previousRoute = routeRef.current;
      routeRef.current = pathname;
      routeStartedAtRef.current = Date.now();
      pageActiveRef.current = true;
      // 首个 page_view 即首次活跃：emit 内部完成 daily ensure 与会话 ensure/轮转，
      // 不再单独重复 ensure
      emit('page_view', {
        client_surface: 'web',
        route: pathname,
        previous_route: previousRoute,
        query_keys: queryKeys(),
        navigation_type: previousRoute ? 'client_navigation' : 'entry',
        locale: document.documentElement.lang || null,
        device_type: deviceType(),
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
      });
    }

    if (document.visibilityState === 'visible') {
      scheduleMidnightEnsure();
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearMidnightTimer();
        emitExit('hidden');
        return;
      }
      if (document.visibilityState === 'visible') {
        scheduleMidnightEnsure();
        if (routeRef.current && !pageActiveRef.current) {
          // visible 恢复：emit 内部先等当次 daily attempt settle（可能已跨上海日），
          // beginEvent 按最近一次活动判定 30 分钟 hidden/idle 轮转
          routeStartedAtRef.current = Date.now();
          pageActiveRef.current = true;
          emit('page_resumed', { route: routeRef.current });
        }
      }
    };

    const handleUnload = () => emitExit('unload');

    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const actionElement = target.closest<HTMLElement>('[data-journey-action]');
      if (actionElement) {
        emit('action_triggered', {
          route: routeRef.current,
          action: actionElement.dataset.journeyAction,
          element_id: actionElement.id || null,
        });
        return;
      }

      const link = target.closest<HTMLAnchorElement>('a[href]');
      if (!link) return;
      try {
        const destination = new URL(link.href, window.location.href);
        if (destination.origin !== window.location.origin) return;
        emit('navigation_clicked', {
          route: routeRef.current,
          destination_route: destination.pathname,
          query_keys: Array.from(new Set(destination.searchParams.keys())).sort(),
          element_id: link.id || null,
        });
      } catch {
        // 无效链接不影响点击本身。
      }
    };

    const handleSubmit = (event: SubmitEvent) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      emit('form_submitted', {
        route: routeRef.current,
        form_id: form.id || null,
        action: form.dataset.journeyAction || null,
      });
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', handleUnload);
    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);

    return () => {
      // 清 timer/监听即可：同用户的 reliability 实例跨路由保活，不随 effect 销毁
      clearMidnightTimer();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', handleUnload);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('submit', handleSubmit, true);
    };
  }, [isAuthenticated, pathname, user?.id, user?.isAdmin]);

  return null;
}
